import createDebug from 'debug';
import { AlertTriangle, Globe, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
	disableQueuePublicAccess,
	enableQueuePublicAccess,
	getQueuePublicAccess,
	type PublicAccessStatus,
	updateQueuePublicAccess,
} from '@/api/public-access';
import { CopyUrlButton } from '@/components/CopyUrlButton';
import { PublicAccessBadge } from '@/components/PublicAccessBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const debug = createDebug('app:queue-public-access');

interface QueuePublicAccessPanelProps {
	queueId: number;
}

export function QueuePublicAccessPanel({ queueId }: QueuePublicAccessPanelProps) {
	const [loading, setLoading] = useState(true);
	const [updating, setUpdating] = useState(false);
	const [status, setStatus] = useState<PublicAccessStatus | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showConfirmDisable, setShowConfirmDisable] = useState(false);

	// Local state for permission checkboxes
	const [canRead, setCanRead] = useState(true);
	const [canWrite, setCanWrite] = useState(false);
	const [canDelete, setCanDelete] = useState(false);

	// Fetch current status
	useEffect(() => {
		async function fetchStatus() {
			try {
				setLoading(true);
				setError(null);
				const data = await getQueuePublicAccess(queueId);
				setStatus(data);
				if (data.enabled) {
					setCanRead(data.canRead ?? true);
					setCanWrite(data.canWrite ?? false);
					setCanDelete(data.canDelete ?? false);
				}
				debug('fetched status: %o', data);
			} catch (err) {
				const message = err instanceof Error ? err.message : String(err);
				setError(message);
				debug('fetch error: %s', message);
			} finally {
				setLoading(false);
			}
		}
		fetchStatus();
	}, [queueId]);

	async function handleTogglePublicAccess(enabled: boolean) {
		if (!enabled && status?.enabled && !showConfirmDisable) {
			// Show confirmation when disabling (only if not already shown)
			setShowConfirmDisable(true);
			return;
		}

		try {
			setUpdating(true);
			setError(null);
			if (enabled) {
				// Enable public access
				const result = await enableQueuePublicAccess(queueId, { canRead, canWrite, canDelete });
				setStatus({
					enabled: true,
					token: result.token,
					url: result.url,
					canRead,
					canWrite,
					canDelete,
					useCount: 0,
				});
				debug('enabled public access: %o', result);
			} else {
				// Disable public access
				await disableQueuePublicAccess(queueId);
				setStatus({ enabled: false });
				setShowConfirmDisable(false);
				debug('disabled public access');
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			setError(message);
			debug('toggle error: %s', message);
		} finally {
			setUpdating(false);
		}
	}

	async function handleUpdatePermissions() {
		if (!status?.enabled) return;

		try {
			setUpdating(true);
			setError(null);
			await updateQueuePublicAccess(queueId, { canRead, canWrite, canDelete });
			setStatus({ ...status, canRead, canWrite, canDelete });
			debug('updated permissions: canRead=%s canWrite=%s canDelete=%s', canRead, canWrite, canDelete);
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			setError(message);
			debug('update error: %s', message);
		} finally {
			setUpdating(false);
		}
	}

	if (loading) {
		return (
			<div className="rounded-lg border bg-muted/30 p-4">
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<Loader2 className="size-4 animate-spin" />
					<span>Loading public access status...</span>
				</div>
			</div>
		);
	}

	const permissionsChanged =
		status?.enabled && (status.canRead !== canRead || status.canWrite !== canWrite || status.canDelete !== canDelete);

	return (
		<div className="rounded-lg border bg-muted/30 p-4 space-y-4">
			{/* Header with toggle */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Globe className="size-5 text-muted-foreground" />
					<h3 className="text-sm font-semibold">Public Access</h3>
					{status?.enabled && (
						<PublicAccessBadge
							canRead={status.canRead}
							canWrite={status.canWrite}
							canDelete={status.canDelete}
							useCount={status.useCount}
						/>
					)}
				</div>
				<Switch
					checked={status?.enabled ?? false}
					onCheckedChange={handleTogglePublicAccess}
					disabled={updating}
				/>
			</div>

			{/* Error display */}
			{error && (
				<div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
					<AlertTriangle className="size-4 mt-0.5 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			{/* Confirmation dialog for disabling */}
			{showConfirmDisable && (
				<div className="rounded-md border border-orange-500/30 bg-orange-50 dark:bg-orange-950/20 p-4 space-y-3">
					<div className="flex items-start gap-2">
						<AlertTriangle className="size-5 text-orange-600 dark:text-orange-500 mt-0.5 shrink-0" />
						<div className="space-y-1">
							<p className="text-sm font-medium text-orange-900 dark:text-orange-200">
								Disable Public Access?
							</p>
							<p className="text-xs text-orange-800 dark:text-orange-300">
								This will revoke the public URL. Anyone with the link will no longer be able to
								access this queue.
							</p>
						</div>
					</div>
					<div className="flex gap-2">
						<Button
							size="sm"
							variant="destructive"
							onClick={() => handleTogglePublicAccess(false)}
							disabled={updating}
						>
							{updating ? <Loader2 className="size-3 mr-1 animate-spin" /> : null}
							Disable
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => setShowConfirmDisable(false)}
							disabled={updating}
						>
							Cancel
						</Button>
					</div>
				</div>
			)}

			{/* Enabled state - show permissions and URL */}
			{status?.enabled && !showConfirmDisable && (
				<div className="space-y-4">
					{/* Permission checkboxes */}
					<div className="space-y-2">
						<Label className="text-xs font-medium text-muted-foreground">Permissions</Label>
						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-2">
								<Checkbox
									id="can-read"
									checked={canRead}
									onCheckedChange={(checked: boolean) => setCanRead(checked === true)}
								/>
								<Label htmlFor="can-read" className="text-sm font-normal cursor-pointer">
									Allow Read (view queue items)
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="can-write"
									checked={canWrite}
									onCheckedChange={(checked: boolean) => setCanWrite(checked === true)}
								/>
								<Label htmlFor="can-write" className="text-sm font-normal cursor-pointer">
									Allow Write (add items to queue)
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="can-delete"
									checked={canDelete}
									onCheckedChange={(checked: boolean) => setCanDelete(checked === true)}
								/>
								<Label htmlFor="can-delete" className="text-sm font-normal cursor-pointer">
									Allow Delete (remove items from queue)
								</Label>
							</div>
						</div>
						{permissionsChanged && (
							<Button size="sm" onClick={handleUpdatePermissions} disabled={updating}>
								{updating ? <Loader2 className="size-3 mr-1 animate-spin" /> : null}
								Update Permissions
							</Button>
						)}
					</div>

					{/* Public URL */}
					{status.url && (
						<div className="space-y-2">
							<Label className="text-xs font-medium text-muted-foreground">Public URL</Label>
							<div className="flex items-center gap-2">
								<code className="flex-1 rounded-md bg-muted px-3 py-2 text-xs font-mono break-all">
									{status.url}
								</code>
								<CopyUrlButton url={status.url} />
							</div>
						</div>
					)}

					{/* Usage stats */}
					{status.useCount !== undefined && status.useCount > 0 && (
						<div className="text-xs text-muted-foreground">
							<span className="font-medium">{status.useCount}</span> view
							{status.useCount !== 1 ? 's' : ''}
							{status.lastUsedAt && (
								<>
									{' '}
									- Last used{' '}
									{new Date(status.lastUsedAt * 1000).toLocaleDateString('en-US', {
										month: 'short',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
									})}
								</>
							)}
						</div>
					)}
				</div>
			)}

			{/* Disabled state - show description */}
			{!status?.enabled && !showConfirmDisable && (
				<p className="text-xs text-muted-foreground">
					Enable public access to share this queue with anyone. You can control whether they can
					view or add items.
				</p>
			)}
		</div>
	);
}
